import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Container, Row, Col, Card, Badge, ProgressBar, ListGroup, Spinner, Nav, Table, Button, Modal, Alert } from 'react-bootstrap';
import {
  BarChart,
  Bar,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import Navbar from "./Navbar";
import { API_ENDPOINTS } from './config/api';
import PageTitle from './components/PageTitle';
import UserAvatar from './components/UserAvatar';
import { DEFAULT_ROTATION_COLORS, getRotationTypesForSpecialty } from './utils/rotationTypesConfig';
import moment from "moment";
import { VACCINATION_CARD_ITEM_NAME } from './constants/vaccinationCardCatalog';


function formatResidentAddress(address) {
  if (address == null) return "N/A";
  if (typeof address === "string") return address.trim() || "N/A";
  if (typeof address !== "object") return "N/A";

  // Helper to capitalize (first letter upper, rest lower)
  const capitalize = (str) =>
    typeof str === "string"
      ? str.charAt(0).toUpperCase() + str.slice(1).toLowerCase()
      : "";

  let main = "";
  if (address.street) main += address.street;
  if (address.apt) {
    if (main.length) main += " ";
    main += `Apt ${address.apt}`;
  }

  // city, state, zip: "City STATE ZIP"
  const city =
    address.city && typeof address.city === "string"
      ? capitalize(address.city)
      : "";
  const state =
    address.state && typeof address.state === "string"
      ? address.state.toUpperCase()
      : "";
  const zip = address.zip ? address.zip : "";

  let location = "";
  if (city) location += city;
  if (state) location += (location.length ? " " : "") + state;
  if (zip) location += (location.length ? " " : "") + zip;

  // Combine as "316 N 35th Apt 208, Omaha NM 63135"
  let formatted = "";
  if (main) formatted = main;
  if (location) formatted += (formatted.length ? ", " : "") + location;

  return formatted.length > 0 ? formatted : "N/A";
}

function normalizeEmergencyContact(emergencyContact) {
  if (emergencyContact) {
    if (typeof emergencyContact === "string") {
      if (emergencyContact === "[object Object]") {
        return { name: "", phone: "", relation: ""};
      }
      try {
        const parsed = JSON.parse(emergencyContact);
        return {
          name: parsed?.name || "",
          phone: parsed?.phone || "",
          relation: parsed?.relation || "",
        };
      } catch (e) {
        console.error("Error parsing emergency contact:", e);
        return { name: "", phone: "", relation: ""};
      }
    }
    if (typeof emergencyContact === "object" && emergencyContact !== null) {
      return {
        name: emergencyContact.name || "",
        phone: emergencyContact.phone || "",
        relation: emergencyContact.relation || "",
      };
    }
  }
  return { name: "", phone: "", relation: ""};
}

const ResidentProfile = () => {
  const { id } = useParams();
  const [resident, setResident] = useState(null);
  const [error, setError] = useState(null);
  const [workHourData, setWorkHourData] = useState([]);
  const [caseLogData, setCaseLogData] = useState([]);
  const [evaluationData, setEvaluationData] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);
  const [pastMonthlyCompleted, setPastMonthlyCompleted] = useState([]);
  const [pastMonthlyCompletedLoading, setPastMonthlyCompletedLoading] = useState(false);
  const [dailyWorkHours30Days, setDailyWorkHours30Days] = useState([]);
  const [dailyWorkHours30DaysLoading, setDailyWorkHours30DaysLoading] = useState(false);
  const [conferenceMonthSessions, setConferenceMonthSessions] = useState([]);
  const [conferenceMonthAttendanceIndex, setConferenceMonthAttendanceIndex] = useState(new Map());
  const [conferenceMonthLoading, setConferenceMonthLoading] = useState(false);
  const [rotationCallInfo, setRotationCallInfo] = useState(null);
  const [rotationCallLoading, setRotationCallLoading] = useState(false);
  const [rotationColors, setRotationColors] = useState(DEFAULT_ROTATION_COLORS);
  const [programSpecialty, setProgramSpecialty] = useState("");
  const [programDuration, setProgramDuration] = useState(null);
  const [programName, setProgramName] = useState("");
  const [inServiceExams, setInServiceExams] = useState([]);
  const [promotionEvidence, setPromotionEvidence] = useState([]);
  const [promotionRequirements, setPromotionRequirements] = useState([]);
  const [academicYear, setAcademicYear] = useState(new Date().getFullYear());
  const [loading, setLoading] = useState(true);
  const [showResidentInfoModal, setShowResidentInfoModal] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem("token");
    const role = localStorage.getItem("role");

    if (!token || role !== "director") {
      setError("Unauthorized access");
      return;
    }

    const fetchData = async () => {
      try {
        // Fetch resident data
        const residentRes = await fetch(`${API_ENDPOINTS.DIRECTOR}/resident/${id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (!residentRes.ok) {
          throw new Error(`Failed to fetch resident data: ${residentRes.status}`);
        }

        const residentData = await residentRes.json();
        setResident(residentData);

        const academicYearForFetch = new Date().getFullYear();
        setAcademicYear(academicYearForFetch);

        // Fetch in-service exams and promotion evidence/requirements
        const headers = { Authorization: `Bearer ${token}` };
        setRotationCallLoading(true);
        const [examsRes, requirementsRes, evidenceRes, rotationSchedulesRes, weekendCallRes, programDetailsRes] = await Promise.all([
          fetch(`${API_ENDPOINTS.PROMOTIONS}/inservice-exams/${id}?academicYear=${academicYearForFetch}`, { headers }),
          fetch(`${API_ENDPOINTS.PROMOTIONS}/requirements/academic-year/${academicYearForFetch}`, { headers }),
          fetch(`${API_ENDPOINTS.PROMOTIONS}/requirements-evidence`, { headers }),
          fetch(`${API_ENDPOINTS.SCHEDULES}/rotations`, { headers }),
          fetch(`${API_ENDPOINTS.SCHEDULES}/weekend-call`, { headers }),
          fetch(`${API_ENDPOINTS.PROGRAMS}/my-program-details`, { headers }),
        ]);

        if (examsRes.ok) {
          const examsData = await examsRes.json();
          setInServiceExams(examsData.results || []);
        }

        if (requirementsRes.ok) {
          const reqData = await requirementsRes.json();
          setPromotionRequirements(reqData.requirements || []);
        }

        if (evidenceRes.ok) {
          const evidenceData = await evidenceRes.json();
          setPromotionEvidence(evidenceData.evidence || []);
        }

        // Rotation colors based on program specialty (dashboard parity)
        try {
          if (programDetailsRes.ok) {
            const programDetails = await programDetailsRes.json();
            const resolvedProgramName = programDetails?.name || programDetails?.program?.name || "";
            const specialty = programDetails?.specialty || programDetails?.program?.specialty;
            const duration = programDetails?.duration || programDetails?.program?.duration || programDetails?.maxPGYLevel || programDetails?.program?.maxPGYLevel || null;
            setProgramName(resolvedProgramName);
            setProgramSpecialty(specialty || "");
            setProgramDuration(duration || null);
            if (specialty) {
              const { colors } = getRotationTypesForSpecialty(specialty);
              const withOverrides = { ...(colors || DEFAULT_ROTATION_COLORS) };
              // Explicit override: Night float should be gray
              withOverrides["Night float"] = "#6B7280";
              withOverrides["night float"] = "#6B7280";
              setRotationColors(withOverrides);
            } else {
              const fallback = { ...DEFAULT_ROTATION_COLORS, "Night float": "#6B7280", "night float": "#6B7280" };
              setRotationColors(fallback);
            }
          } else {
            setProgramName("");
            setProgramSpecialty("");
            setProgramDuration(null);
            const fallback = { ...DEFAULT_ROTATION_COLORS, "Night float": "#6B7280", "night float": "#6B7280" };
            setRotationColors(fallback);
          }
        } catch (colorErr) {
          console.error("Error setting rotation colors:", colorErr);
          setProgramName("");
          setProgramSpecialty("");
          setProgramDuration(null);
          const fallback = { ...DEFAULT_ROTATION_COLORS, "Night float": "#6B7280", "night float": "#6B7280" };
          setRotationColors(fallback);
        }

        // Rotation + weekend call info (computed client-side, similar to ResidentDashboard)
        try {
          const [rotationSchedulesRaw, weekendCallRaw] = await Promise.all([
            rotationSchedulesRes.ok ? rotationSchedulesRes.json() : Promise.resolve({}),
            weekendCallRes.ok ? weekendCallRes.json() : Promise.resolve({}),
          ]);

          const formatDateYmd = (date) => {
            const y = date.getFullYear();
            const m = String(date.getMonth() + 1).padStart(2, "0");
            const d = String(date.getDate()).padStart(2, "0");
            return `${y}-${m}-${d}`;
          };

          const getWeekStartForDate = (dateInput) => {
            const date = new Date(dateInput.getFullYear(), dateInput.getMonth(), dateInput.getDate());
            const day = date.getDay();
            const daysToMonday = day === 0 ? 6 : day - 1;
            const monday = new Date(date);
            monday.setDate(monday.getDate() - daysToMonday);
            return formatDateYmd(monday);
          };

          const scheduleRecord = rotationSchedulesRaw?.[residentData?.year];
          const scheduleType = scheduleRecord?.scheduleType || "monthly";
          const isWeeklyOrBlocks = scheduleType === "weekly" || scheduleType === "blocks";
          const residentKey = String(id);
          const residentSchedule = scheduleRecord?.schedule?.[residentKey] || null;

          const getRotationForDate = (dateObj) => {
            if (!residentSchedule?.rotations) return "Not Assigned";
            if (isWeeklyOrBlocks) {
              const weekKey = getWeekStartForDate(dateObj);
              return residentSchedule.rotations?.[weekKey] || "Not Assigned";
            }
            const monthKey = `${dateObj.getFullYear()}-${String(dateObj.getMonth() + 1).padStart(2, "0")}`;
            return residentSchedule.rotations?.[monthKey] || "Not Assigned";
          };

          const today = new Date();
          const currentRotation = getRotationForDate(today);
          const upcomingDate = new Date(today);
          upcomingDate.setDate(upcomingDate.getDate() + (isWeeklyOrBlocks ? 7 : 1));
          if (!isWeeklyOrBlocks) {
            // for monthly, jump to first day of next month
            const nm = new Date(today.getFullYear(), today.getMonth() + 1, 1);
            upcomingDate.setTime(nm.getTime());
          }
          const upcomingRotation = getRotationForDate(upcomingDate);

          const periodFor = (which) => {
            if (isWeeklyOrBlocks) {
              const base = which === "upcoming" ? new Date(today.getFullYear(), today.getMonth(), today.getDate() + 7) : today;
              const day = base.getDay();
              const daysToMonday = day === 0 ? 6 : day - 1;
              const monday = new Date(base);
              monday.setDate(base.getDate() - daysToMonday);
              const sunday = new Date(monday);
              sunday.setDate(monday.getDate() + 6);
              return { start: formatDateYmd(monday), end: formatDateYmd(sunday) };
            }
            if (which === "upcoming") {
              const start = new Date(today.getFullYear(), today.getMonth() + 1, 1);
              const end = new Date(today.getFullYear(), today.getMonth() + 2, 0);
              return { start: formatDateYmd(start), end: formatDateYmd(end) };
            }
            const start = new Date(today.getFullYear(), today.getMonth(), 1);
            const end = new Date(today.getFullYear(), today.getMonth() + 1, 0);
            return { start: formatDateYmd(start), end: formatDateYmd(end) };
          };

          const buildWeekendCallForWeek = (weekStartDate) => {
            const saturday = new Date(weekStartDate);
            saturday.setDate(weekStartDate.getDate() + 5);
            const sunday = new Date(weekStartDate);
            sunday.setDate(weekStartDate.getDate() + 6);

            const saturdayStr = formatDateYmd(saturday);
            const sundayStr = formatDateYmd(sunday);

            const saturdayRotation = getRotationForDate(saturday);
            const sundayRotation = getRotationForDate(sunday);

            const saturdayCall = saturdayRotation !== "Not Assigned"
              ? weekendCallRaw?.[saturdayRotation]?.[saturdayStr] || null
              : null;
            const sundayCall = sundayRotation !== "Not Assigned"
              ? weekendCallRaw?.[sundayRotation]?.[sundayStr] || null
              : null;

            if (!saturdayCall && !sundayCall) return null;
            return { saturday: saturdayCall, sunday: sundayCall, saturdayRotation, sundayRotation };
          };

          const currentDay = today.getDay();
          const daysToMonday = currentDay === 0 ? 6 : currentDay - 1;
          const currentWeekStart = new Date(today);
          currentWeekStart.setDate(today.getDate() - daysToMonday);
          const nextWeekStart = new Date(currentWeekStart);
          nextWeekStart.setDate(currentWeekStart.getDate() + 7);

          setRotationCallInfo({
            scheduleType,
            currentRotation,
            upcomingRotation,
            currentPeriod: periodFor("current"),
            upcomingPeriod: periodFor("upcoming"),
            currentWeekendCall: buildWeekendCallForWeek(currentWeekStart),
            upcomingWeekendCall: buildWeekendCallForWeek(nextWeekStart),
          });
        } catch (rotationErr) {
          console.error("Error computing rotation/call info:", rotationErr);
          setRotationCallInfo(null);
        } finally {
          setRotationCallLoading(false);
        }

        // Fetch work hour logs
        // Fetch last 30 days work hour logs for daily summaries and charts
        const toYmdLocal = (dateObj) => {
          const y = dateObj.getFullYear();
          const m = String(dateObj.getMonth() + 1).padStart(2, "0");
          const d = String(dateObj.getDate()).padStart(2, "0");
          return `${y}-${m}-${d}`;
        };

        const endDate = new Date();
        const startDate = new Date();
        startDate.setDate(startDate.getDate() - 29);
        const startStr = toYmdLocal(startDate);
        const endStr = toYmdLocal(endDate);

        setDailyWorkHours30DaysLoading(true);
        const last30Res = await fetch(
          `${API_ENDPOINTS.DIRECTOR}/work-log-detail-range?residentId=${id}&startDate=${startStr}&endDate=${endStr}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (last30Res.ok) {
          const last30Data = await last30Res.json();
          const weeks = last30Data?.weeks || [];
          setWorkHourData(weeks);

          // Build daily series for the bar chart + weekly-like stats
          const dayEntryMap = new Map();
          (weeks || []).forEach((wk) => {
            (wk?.days || []).forEach((day) => {
              if (!day?.date) return;
              const dateKey = toYmdLocal(new Date(day.date));
              const rawTotal = day?.total;
              const hasAttendance = rawTotal != null || day?.leaveType != null;

              let hours = 0;
              if (typeof rawTotal === "string" && rawTotal.includes(":")) {
                const [h, m] = rawTotal.split(":").map((n) => parseInt(n, 10));
                hours = (h || 0) + (m || 0) / 60;
              }

              dayEntryMap.set(dateKey, {
                dateKey,
                hours,
                hasAttendance,
                isWeekend: new Date(day.date).getDay() === 0 || new Date(day.date).getDay() === 6,
              });
            });
          });

          const daily = Array.from({ length: 30 }, (_, i) => {
            const dt = new Date(startDate);
            dt.setDate(dt.getDate() + i);
            const dateKey = toYmdLocal(dt);
            const entry = dayEntryMap.get(dateKey);

            return {
              date: dateKey,
              day: moment(dt).format("MMM D"),
              hours: entry ? entry.hours : 0,
              hasData: entry ? entry.hasAttendance : false,
              isWeekend: dt.getDay() === 0 || dt.getDay() === 6,
            };
          });

          setDailyWorkHours30Days(daily);
        } else {
          setWorkHourData([]);
          setDailyWorkHours30Days([]);
        }
        setDailyWorkHours30DaysLoading(false);

        // Fetch case logs
        const caseLogsRes = await fetch(`${API_ENDPOINTS.DIRECTOR}/resident-case-logs/${id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (caseLogsRes.ok) {
          const caseLogsData = await caseLogsRes.json();
          setCaseLogData(caseLogsData);
        }

        // Fetch evaluation data
        const evaluationRes = await fetch(`${API_ENDPOINTS.DIRECTOR}/resident-evaluations/${id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (evaluationRes.ok) {
          const evaluationData = await evaluationRes.json();
          setEvaluationData(evaluationData);
        }

        // Simulate recent activity data (replace with actual API call)
        setRecentActivity([
          { type: 'case_log', description: 'Completed case in General Surgery', date: new Date() },
          { type: 'evaluation', description: 'Submitted monthly evaluation', date: new Date(Date.now() - 86400000) },
          { type: 'work_hours', description: 'Submitted work hours for week 20', date: new Date(Date.now() - 172800000) },
        ]);

        setLoading(false);
      } catch (err) {
        setError(err.message);
        console.error("Error fetching data:", err);
        setDailyWorkHours30DaysLoading(false);
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);

  // Fetch past monthly work-log submissions (director summary returns status: complete/pending/incomplete)
  useEffect(() => {
    const run = async () => {
      try {
        if (!resident) return;
        const token = localStorage.getItem("token");
        if (!token) return;

        const residentIdStr = String(resident?.id ?? id);
        const now = new Date();
        const monthsToCheck = [];
        for (let i = 1; i <= 6; i++) {
          const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
          monthsToCheck.push({ month: d.getMonth(), year: d.getFullYear() });
        }

        setPastMonthlyCompletedLoading(true);
        const results = await Promise.all(
          monthsToCheck.map(async ({ month, year }) => {
            const res = await fetch(
              `${API_ENDPOINTS.DIRECTOR}/work-logs-summary?month=${month}&year=${year}`,
              { headers: { Authorization: `Bearer ${token}` } }
            );
            if (!res.ok) return null;
            const data = await res.json();
            const match = Array.isArray(data)
              ? data.find((r) => String(r.id) === residentIdStr)
              : null;

            if (match?.status === "complete") {
              return {
                month,
                year,
                lastSubmission: match.lastSubmission || null,
              };
            }
            return null;
          })
        );

        const completed = results.filter(Boolean).sort((a, b) => {
          if (a.year !== b.year) return b.year - a.year;
          return b.month - a.month;
        });
        setPastMonthlyCompleted(completed);
      } catch (err) {
        console.error("Failed fetching past monthly completed work hours:", err);
        setPastMonthlyCompleted([]);
      } finally {
        setPastMonthlyCompletedLoading(false);
      }
    };

    run();
  }, [resident, id]);

  // Conference attendance (this month): list sessions + attended/not attended/pending
  useEffect(() => {
    const run = async () => {
      try {
        if (!resident) return;
        const token = localStorage.getItem("token");
        if (!token) return;

        const now = new Date();
        const yearMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
        const monthStart = moment(`${yearMonth}-01`).startOf("month");
        const monthEnd = monthStart.clone().endOf("month");

        setConferenceMonthLoading(true);
        const headers = { Authorization: `Bearer ${token}` };

        const [confsRes, attendanceRes] = await Promise.all([
          fetch(`${API_ENDPOINTS.CONFERENCES}/month/${yearMonth}`, { headers }),
          fetch(`${API_ENDPOINTS.CONFERENCE_ATTENDANCE}/user/${id}`, { headers }),
        ]);

        const conferences = confsRes.ok ? await confsRes.json() : [];
        const attendance = attendanceRes.ok ? await attendanceRes.json() : [];

        const attendanceIndex = new Map();
        (attendance || []).forEach((a) => {
          const sid = a?.sessionId ?? a?.session?.id;
          if (!sid) return;
          const sessionDate = a?.session?.date;
          if (sessionDate) {
            const m = moment(sessionDate);
            if (m.isBefore(monthStart, "day") || m.isAfter(monthEnd, "day")) return;
          }
          attendanceIndex.set(String(sid), a);
        });

        const sessions = (Array.isArray(conferences) ? conferences : [])
          .flatMap((conf) => {
            const confTitle = conf?.title || "Conference";
            const confType = conf?.type || "";
            const confStartTime = conf?.startTime || "";
            const confEndTime = conf?.endTime || "";
            return (conf?.sessions || []).map((s) => ({
              id: s.id,
              date: s.date,
              location: s.location || "",
              talkTitle: s.talkTitle || "",
              speaker: s.speaker || "",
              conferenceTitle: confTitle,
              conferenceType: confType,
              startTime: confStartTime,
              endTime: confEndTime,
              isCancelled: !!s.isCancelled,
            }));
          })
          .filter((s) => s?.date)
          .filter((s) => {
            const m = moment(s.date);
            return m.isSameOrAfter(monthStart, "day") && m.isSameOrBefore(monthEnd, "day");
          })
          .sort((a, b) => {
            const aDt = moment(`${a.date} ${a.startTime || "00:00"}`, "YYYY-MM-DD HH:mm");
            const bDt = moment(`${b.date} ${b.startTime || "00:00"}`, "YYYY-MM-DD HH:mm");
            return aDt.valueOf() - bDt.valueOf();
          });

        setConferenceMonthSessions(sessions);
        setConferenceMonthAttendanceIndex(attendanceIndex);
      } catch (err) {
        console.error("Error fetching conference attendance:", err);
        setConferenceMonthSessions([]);
        setConferenceMonthAttendanceIndex(new Map());
      } finally {
        setConferenceMonthLoading(false);
      }
    };

    run();
  }, [resident, id]);

  const formatYmdRange = (period) => {
    if (!period?.start || !period?.end) return "N/A";
    const start = new Date(`${period.start}T00:00:00`);
    const end = new Date(`${period.end}T00:00:00`);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return "N/A";
    return `${start.toLocaleDateString(undefined, { month: "short", day: "numeric" })} - ${end.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}`;
  };

  

  // Calculate performance metrics
  const calculateMetrics = () => {
    const totalCases = caseLogData.length;
    const totalWorkHours = workHourData.reduce((sum, week) => {
      const [hours, minutes] = week.weekTotal.split(':').map(Number);
      return sum + hours + minutes / 60;
    }, 0);
    const evaluationCompletion = evaluationData.length > 0 
      ? (evaluationData.filter(e => e.completed).length / evaluationData.length) * 100 
      : 0;

    return {
      totalCases,
      totalWorkHours,
      evaluationCompletion
    };
  };

  const startOfDay = (d) => {
    const x = new Date(d);
    x.setHours(0, 0, 0, 0);
    return x;
  };

  const getDefaultRecurrenceType = (categoryKey) => {
    if (categoryKey === 'licensesCertifications') return 'until_expiration_until_graduation';
    if (categoryKey === 'trainingCertifications' || categoryKey === 'vaccinations') return 'yearly_until_graduation';
    if (categoryKey === 'vaccinationCard') return 'until_complete';
    return 'until_complete';
  };

  const isApprovedEvidenceValid = (categoryKey, requiredItem, evidence) => {
    const recurrenceType = requiredItem?.recurrenceType || getDefaultRecurrenceType(categoryKey);
    if (recurrenceType === 'until_complete') return true;

    const effectiveRaw = evidence?.reviewedAt || evidence?.updatedAt;
    const effective = effectiveRaw ? new Date(effectiveRaw) : null;
    if (!effective || Number.isNaN(effective.getTime())) return false;
    const effectiveSod = startOfDay(effective);

    if (recurrenceType === 'yearly_until_graduation') {
      const due = new Date(effectiveSod);
      due.setFullYear(due.getFullYear() + 1);
      return startOfDay(due).getTime() >= startOfDay(new Date()).getTime();
    }

    if (recurrenceType === 'until_expiration_until_graduation') {
      if (!evidence?.expirationDate) return false;
      const exp = startOfDay(new Date(evidence.expirationDate));
      return exp.getTime() >= startOfDay(new Date()).getTime();
    }

    // Back-compat
    if (categoryKey === 'licensesCertifications' && evidence?.expirationDate) {
      const exp = startOfDay(new Date(evidence.expirationDate));
      return exp.getTime() >= startOfDay(new Date()).getTime();
    }

    return true;
  };

  const programWide = (() => {
    const source = Array.isArray(promotionRequirements) ? promotionRequirements[0] : null;
    const pgyPromotionRow = Array.isArray(promotionRequirements)
      ? promotionRequirements.find((r) => r.pgyLevel === resident?.year)
      : null;
    const categories = [
      { key: 'licensesCertifications', label: 'Licenses & Certifications' },
      { key: 'onboardingRequirements', label: 'Onboarding Requirements' },
      { key: 'trainingCertifications', label: 'Training Certifications' },
      { key: 'vaccinations', label: 'Vaccinations' },
      { key: 'scholarlyActivity', label: 'Scholarly Activity' },
    ];
    if (pgyPromotionRow?.vaccinationCardRequired) {
      categories.push({ key: 'vaccinationCard', label: 'Vaccination card' });
    }

    const requiredItemsByCategory = categories.reduce((acc, c) => {
      if (c.key === 'vaccinationCard') {
        acc[c.key] = [{ name: VACCINATION_CARD_ITEM_NAME, required: true, recurrenceType: 'until_complete' }];
        return acc;
      }
      const items = Array.isArray(source?.[c.key]) ? source[c.key].filter((it) => it && it.required) : [];
      acc[c.key] = items;
      return acc;
    }, {});

    const neededItemKeys = new Set();
    Object.entries(requiredItemsByCategory).forEach(([categoryKey, items]) => {
      (items || []).forEach((it) => neededItemKeys.add(`${categoryKey}::${it.name}`));
    });

    // Keep latest evidence per (category::itemName) for THIS resident
    const latestEvidenceIndex = new Map();
    promotionEvidence.forEach((ev) => {
      const rid = ev?.residentId;
      if (rid == null || String(rid) !== String(id)) return;

      const itemKey = `${ev.category}::${ev.itemName}`;
      if (!neededItemKeys.has(itemKey)) return;

      const existing = latestEvidenceIndex.get(itemKey);
      const evTime = new Date(ev.updatedAt || ev.reviewedAt || 0).getTime();
      const existingTime = new Date(existing?.updatedAt || existing?.reviewedAt || 0).getTime();
      if (!existing || evTime > existingTime) {
        latestEvidenceIndex.set(itemKey, ev);
      }
    });

    const categoryStats = categories.map((c) => {
      const items = requiredItemsByCategory[c.key] || [];
      const total = items.length;
      const completed = items.reduce((count, it) => {
        const evidence = latestEvidenceIndex.get(`${c.key}::${it.name}`) || null;
        const approved = evidence?.status === 'approved';
        const valid = approved ? isApprovedEvidenceValid(c.key, it, evidence) : false;
        return count + (approved && valid ? 1 : 0);
      }, 0);

      const itemsWithStatus = items.map((it) => {
        const evidence = latestEvidenceIndex.get(`${c.key}::${it.name}`) || null;
        const approved = evidence?.status === 'approved';
        const valid = approved ? isApprovedEvidenceValid(c.key, it, evidence) : false;
        const recurrenceType = it?.recurrenceType || getDefaultRecurrenceType(c.key);

        let statusLabel = 'Pending';
        let badgeVariant = 'secondary';

        if (!evidence) {
          statusLabel = 'Pending';
          badgeVariant = 'secondary';
        } else if (evidence?.status === 'rejected') {
          statusLabel = 'Rejected';
          badgeVariant = 'danger';
        } else if (approved && valid) {
          statusLabel = 'Approved';
          badgeVariant = 'success';
        } else if (approved && !valid) {
          statusLabel = recurrenceType === 'yearly_until_graduation' ? 'Renewal due' : 'Expired';
          badgeVariant = recurrenceType === 'yearly_until_graduation' ? 'warning' : 'danger';
        } else {
          statusLabel = evidence?.status === 'submitted' ? 'Submitted' : 'Pending';
          badgeVariant = 'info';
        }

        return { name: it.name, statusLabel, badgeVariant };
      });

      return { ...c, total, completed, itemsWithStatus };
    });

    const totalRequired = categoryStats.reduce((sum, c) => sum + c.total, 0);
    const totalCompleted = categoryStats.reduce((sum, c) => sum + c.completed, 0);
    const pct = totalRequired > 0 ? Math.round((totalCompleted / totalRequired) * 100) : 0;

    return { source, categoryStats, totalRequired, totalCompleted, pct };
  })();

  if (error) return <p style={{ color: "red" }}>Error: {error}</p>;
  if (loading) {
    const title = resident
      ? `Resident Profile: ${resident.firstName} ${resident.lastName}`
      : "Resident Profile";
    const pageTitle = resident
      ? `Resident: ${resident.firstName} ${resident.lastName}`
      : "Resident";

    return (
      <div className="bg-light min-vh-100">
        <PageTitle
          title={title}
          description="View resident profile, performance metrics, and statistics"
        />
        <Navbar pageTitle={pageTitle} />

        <div className="bg-white shadow-sm custom-sticky-top">
          <Container className="py-4">
            
          </Container>
        </div>

        <Container className="py-4">
          <div
            className="d-flex flex-column align-items-center justify-content-center"
            style={{ minHeight: 320 }}
          >
            <Spinner animation="border" role="status" />
            <div className="text-muted mt-2">Loading resident data...</div>
          </div>
        </Container>
      </div>
    );
  }

  if (!resident) return <p>No resident data found</p>;

  const metrics = calculateMetrics();
  const residentFullName = [resident.firstName, resident.lastName].filter(Boolean).join(" ") || "N/A";
  const formatPhone = (val) =>
    val
      ? (() => {
          const cleaned = String(val).replace(/\D/g, "");
          if (cleaned.length === 10) {
            return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
          }
          return val;
        })()
      : "N/A";
  const residentPhone = formatPhone(resident.phone);
  const residentPagerNumber = formatPhone(resident.pagerNumber);
  const normalizedEmergencyContact = normalizeEmergencyContact(resident.emergencyContact);
  const emergencyPhone = normalizedEmergencyContact.phone
    ? (() => {
      const cleaned = String(normalizedEmergencyContact.phone).replace(/\D/g, "");
      if (cleaned.length === 10) {
        return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
      }
      return normalizedEmergencyContact.phone;
    })()
    : "N/A";
  const programType =
    programSpecialty ||
    resident.specialty ||
    resident.programType ||
    resident.program?.specialty ||
    resident.program?.name ||
    "Internal Medicine";
  const anticipatedGraduationYear =
    resident.year && programDuration && Number.isFinite(Number(resident.year)) && Number.isFinite(Number(programDuration))
      ? new Date().getFullYear() + Math.max(Number(programDuration) - Number(resident.year), 0)
      : null;

  return (
    <div className="min-vh-100">
      <PageTitle title={`Resident Profile: ${resident.firstName} ${resident.lastName}`} description="View resident profile, performance metrics, and statistics" />
      <Navbar pageTitle={`Resident: ${resident.firstName} ${resident.lastName}`} />
      <div className="bg-white shadow-sm" style={{ borderBottom: '1px dashed var(--border-bg)' }}>
        <Container className="py-2">
          <Row className="align-items-center">
            <Col xs={12} md={12}>
            <div className="d-flex flex-row justify-content-between align-items-center">
              <div className="d-flex flex-row gap-2 align-items-center">
              <UserAvatar 
                    user={resident}
                    size={42}
            /> 
            {/* Name block: only show if both first and last exist */}
                  {(resident.firstName || resident.lastName) && (
                    <div className="d-flex flex-column me-2 ">
                      <h6 className="mb-0 fw-semibold">{resident.firstName} {resident.lastName}</h6>
                      {resident.email && (
                        <div className="text-muted small">  DOB: {new Date(resident.dob).toLocaleDateString()}, {resident.gender}</div>
                      )}
                    </div>
                  )}

                <div className="flex-row gap-4 align-items-center d-md-flex d-none ">
                  {/* Address */}
                  {resident.address &&
                    formatResidentAddress(resident.address) !== "N/A" && (
                    <div className="d-flex flex-column">
                      <span className="text-muted fw-semibold smaller">Address:</span>
                      <span className="fw-semibold small text-truncate">
                        {formatResidentAddress(resident.address)}
                      </span>
                    </div>
                  )}


                  {/* Phone */}
                  {resident.phone && (
                    <div className="d-flex flex-column">
                      <span className="text-muted fw-semibold smaller">Phone:</span>
                      <span className="fw-semibold small">
                        {residentPhone}
                      </span>
                    </div>
                  )}

                  {/* PGY Year */}
                  {resident.year && (
                    <div className="d-flex flex-column">
                      <span className="text-muted fw-semibold smaller">PGY Year:</span>
                      <Badge bg="primary" className="py-1 px-2">{`PGY-${resident.year}`}</Badge>
                    </div>
                  )}

                  {/* Emergency Contact */}
                  {resident.emergencyContact?.name  && (
                    <div className="d-flex flex-column">
                      <span className="text-muted fw-semibold smaller">Emergency Contact:</span>
                      <span className="fw-semibold small">{resident.emergencyContact.name } ({resident.emergencyContact.relation}) {resident.emergencyContact.phone ? `, ${resident.emergencyContact.phone}` : ''}</span>
                    </div>
                  )}
                </div>
                
              </div>
            <div className="d-flex justify-content-end">
            <>
              <Button
                variant="outline-primary"
                size="sm"
                onClick={() => setShowResidentInfoModal(true)}
              >
                <i className="bi bi-card-checklist" />
              </Button>
              {typeof window !== "undefined" && (
                <Modal
                  show={typeof showResidentInfoModal !== "undefined" ? showResidentInfoModal : false}
                  onHide={() => setShowResidentInfoModal(false)}
                  centered
                >
                <Modal.Header className="border-0 text-white px-0 d-block"
                              style={{
                                background:
                                  "linear-gradient(45deg, var(--blue) 0%, #252F4A 60%, var(--green) 100%)",
                                  height: "80px",
                              }}
                            >
                            
                            <div className="d-flex px-4 w-100 align-items-center justify-content-between">
                      <div>
                        <div className="small text-uppercase" style={{ letterSpacing: "0.07em", opacity: 0.9 }}>
                         {programName || "GME Program"}
                        </div>
                        <Modal.Title className="mb-0">Resident ID Card</Modal.Title>
                      </div>
                      <Badge
                        bg="dark-transparent"
                      >
                       PGY-{resident.year || "N/A"}
                      </Badge>
                    </div>
                              <div className="w-100 px-0 mt-2 d-flex  align-items-center justify-content-center" 
                              style={{ background: "linear-gradient(45deg, transparent 0%, var(--card-bg) 100%)",
                                  height: "20px",
                              }}></div>
                  </Modal.Header>
                  <Modal.Body className="p-4 rounded-3 rounded-bottom">
                    
                      <div className="d-flex flex-column flex-md-row gap-4 align-items-md-start mb-4">
                          <UserAvatar user={resident} className="rounded-3" size={84} />

                        <div className="flex-grow-1 ">
                          <h4 className="mb-0 fw-semibold">{residentFullName}</h4>
                          <div className="mb-0 text-primary fs-6">Medical Doctor, {programType}</div>
                          {anticipatedGraduationYear && (
                            <div className="mb-2 text-muted small">
                              Anticipated graduation: June {anticipatedGraduationYear}
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="mb-3">
                        <h6 className="text-uppercase mb-2">Contact</h6>
                        <Row className="g-1 small">
                          <Col md={6}>
                            <div className="fw-semibold">Email</div>
                            <div className="fs-6 text-muted">{resident.email || "N/A"}</div>
                          </Col>
                          <Col md={6}>
                            <div className="fw-semibold">Phone</div>
                            <div className="fs-6 text-muted">{residentPhone}</div>
                          </Col>
                          <Col md={6}>
                            <div className="fw-semibold">Pager Number</div>
                            <div className="fs-6 text-muted">{residentPagerNumber}</div>
                          </Col>
                        </Row>
                      </div>

                      <div className="mb-3">
                        <h6 className="text-uppercase mb-2">Personal</h6>
                        <Row className="g-1 small">
                          <Col md={6}>
                            <div className="fw-semibold">Date of Birth</div>
                            <div className="fs-6 text-muted">
                              {resident.dob ? new Date(resident.dob).toLocaleDateString() : "N/A"}
                            </div>
                          </Col>
                          <Col md={6}>
                            <div className="fw-semibold">Gender</div>
                            <div className="fs-6 text-muted">{resident.gender || "N/A"}</div>
                          </Col>
                          <Col md={12}>
                            <div className="fw-semibold">Home address</div>
                            <div className="fs-6 text-muted">
                              {formatResidentAddress(resident.address)}
                            </div>
                          </Col>
                        </Row>
                      </div>

                      <div>
                        <h6 className="text-uppercase mb-2">Additional Info</h6>
                        <Row className="g-1 small">
                          <Col md={6}>
                            <div className="fw-semibold">NPI Number</div>
                            <div className="fs-6 text-muted">{resident.npiNumber || "N/A"}</div>
                          </Col>
                          <Col md={6}>
                            <div className="fw-semibold">Medical School</div>
                            <div className="fs-6 text-muted">{resident.medicalSchool || "N/A"}</div>
                          </Col>
                        </Row>
                      </div>

                      <Alert variant="danger" className="mt-3 mb-0">
                        <div className="text-uppercase fw-semibold small mb-2">Emergency Contact</div>
                        <Row className="g-1 small">
                          <Col md={6}>
                            <div className="fw-semibold">Name</div>
                            <div className="fs-6">{normalizedEmergencyContact.name || "N/A"}</div>
                          </Col>
                          <Col md={6}>
                            <div className="fw-semibold">Relation</div>
                            <div className="fs-6">{normalizedEmergencyContact.relation || "N/A"}</div>
                          </Col>
                          <Col md={6}>
                            <div className="fw-semibold">Phone</div>
                            <div className="fs-6">{emergencyPhone}</div>
                          </Col>
                        </Row>
                      </Alert>
                    
                  </Modal.Body>
                  <Modal.Footer className="border-0 px-4 d-flex d-sm-none">
                    <Button variant="secondary" size="sm" onClick={() => setShowResidentInfoModal(false)}>Close</Button>
                  </Modal.Footer>
                </Modal>
              )}
            </>
            </div>
            </div>
            </Col>
          </Row>
        </Container></div>
        <div className="bg-white shadow-sm custom-sticky-top">
        <Container>
          <Row>
            <Col md={12}>
            <Nav variant="tabs" defaultActiveKey="basic-information">
              <Nav.Item>
                <Nav.Link eventKey="basic-information">Overview</Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="performance-metrics">Work Hours</Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="performance-metrics">Case Log</Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="performance-metrics">Evaluation</Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="performance-metrics">Recent Activity</Nav.Link>
              </Nav.Item>
            </Nav>
            </Col>
          </Row>

        </Container></div>
      
      <Container className="py-4">

        <Row>
          <Col md={9}>
          <Row>
            <Col md={12}>
              <Card className="mb-4 border-0 shadow-sm">
                <Card.Header className="border-0 p-3 d-flex justify-content-between align-items-center">
                  <div>
                    <h6 className="mb-0 fw-semibold">Rotation & Call Schedule</h6>
                    <div className="text-muted small">Rotation and weekend call schedule</div>
                  </div>
                  {rotationCallLoading ? (
                    <Badge bg="secondary">Loading</Badge>
                  ) : (
                    <Badge bg="light" text="dark">
                      {rotationCallInfo?.scheduleType || "—"}
                    </Badge>
                  )}
                </Card.Header>
                <Card.Body className="pt-0">
                  {!rotationCallInfo && !rotationCallLoading ? (
                    <div className="text-muted small">No rotation/call info available.</div>
                  ) : (
                    <Row className="g-4">
                      <Col md={6}>
                        <div>
                          <div className="border-alt rounded-3 p-3 bg-white mb-3">
                          <div className="d-flex justify-content-between align-items-start mb-2">
                            <div className="fw-semibold small">Current Rotation</div>
                            <Badge bg="primary" className="rounded-2">current</Badge>
                          </div>
                          <div className="d-flex align-items-center gap-2 flex-wrap">
                            <span
                              className="text-white rounded-2 px-2 py-1 fw-semibold small"
                              style={{
                                backgroundColor:
                                  String(rotationCallInfo?.currentRotation || "").toLowerCase() === "night float"
                                    ? "#6B7280"
                                    : (rotationColors?.[rotationCallInfo?.currentRotation] ||
                                      rotationColors?.[String(rotationCallInfo?.currentRotation || "").toLowerCase()] ||
                                      "#3B82F6"),
                              }}
                            >
                              {rotationCallInfo?.currentRotation || "—"}
                            </span>
                          </div>
                          <div className="text-muted small">{formatYmdRange(rotationCallInfo?.currentPeriod)}</div>
                          </div>
                          <div className="rounded-3 p-3 bg-light">
                          <div className="d-flex justify-content-between align-items-start mb-2">
                            <div className="fw-semibold text-muted small">Upcoming Rotation</div>
                            <Badge bg="secondary" className="rounded-2">upcoming</Badge>
                          </div>
                          <div className="d-flex align-items-center gap-2 flex-wrap">
                            <span
                              className="text-white rounded-2 px-2 py-1 fw-semibold small"
                              style={{
                                backgroundColor:
                                  String(rotationCallInfo?.upcomingRotation || "").toLowerCase() === "night float"
                                    ? "#6B7280"
                                    : (rotationColors?.[rotationCallInfo?.upcomingRotation] ||
                                      rotationColors?.[String(rotationCallInfo?.upcomingRotation || "").toLowerCase()] ||
                                      "#F59E0B"),
                              }}
                            >
                              {rotationCallInfo?.upcomingRotation || "—"}
                            </span>
                          </div>
                          <div className="text-muted small">{formatYmdRange(rotationCallInfo?.upcomingPeriod)}</div>
                        </div>
                        </div>
                      </Col>

                      <Col md={6}>
                        <div className="border-alt rounded-3 p-3 h-100 bg-white mb-3">
                          <div className="fw-semibold mb-1 small">Weekend Call Schedule</div>

                          {(() => {
                            const residentFullName = `${resident.firstName} ${resident.lastName}`.trim().toLowerCase();
                            const normalize = (s) => String(s || "").trim().toLowerCase();
                            const isOnCallForDay = (dayData) =>
                              !!dayData?.residents?.some((r) => normalize(r?.name) === residentFullName);

                            const renderWeek = (label, wk) => {
                              if (!wk) return <div className="text-muted small"><em>No weekend call scheduled</em></div>;

                              const sat = wk?.saturday?.type === "Saturday" ? wk.saturday : (wk?.saturday?.type === "Sunday" ? null : wk?.saturday);
                              const sun = wk?.sunday || (wk?.saturday?.type === "Sunday" ? wk.saturday : null);

                              const onSat = isOnCallForDay(sat);
                              const onSun = isOnCallForDay(sun);
                              const onWeek = onSat || onSun;

                              return (
                                <div className=" d-flex gap-2 flex-column">
                                  <div className="d-flex justify-content-between align-items-center">
                                    <div className="text-muted small fw-semibold">{label}</div>
                                    <Badge bg={onWeek ? "warning" : "primary"} className="rounded-2">
                                      {onWeek ? "On call" : "Not on call"}
                                    </Badge>
                                  </div>

                                  {onWeek ? (
                                    <div className="d-flex flex-column gap-2">
                                      {sat && (
                                        <div className="bg-light  d-flex justify-content-between align-items-center rounded-3 p-2">
                                          <div className="small fw-semibold">
                                            Saturday {sat?.date ? `· ${new Date(`${sat.date}T00:00:00`).toLocaleDateString()}` : ""}
                                          </div>
                                          <div className="text-muted small">
                                            {(sat?.residents || []).map((r, idx) => (
                                              <span key={idx} className={normalize(r?.name) === residentFullName ? "fw-semibold text-primary" : ""}>
                                                {r?.name}{idx < (sat.residents || []).length - 1 ? ", " : ""}
                                              </span>
                                            ))}
                                          </div>
                                        </div>
                                      )}
                                      {sun && (
                                        <div className="bg-light d-flex justify-content-between align-items-center rounded-3 p-2">
                                          <div className="small fw-semibold">
                                            Sunday {sun?.date ? `· ${new Date(`${sun.date}T00:00:00`).toLocaleDateString()}` : ""}
                                          </div>
                                          <div className="text-muted small">
                                            {(sun?.residents || []).map((r, idx) => (
                                              <span key={idx} className={normalize(r?.name) === residentFullName ? "fw-semibold text-primary" : ""}>
                                                {r?.name}{idx < (sun.residents || []).length - 1 ? ", " : ""}
                                              </span>
                                            ))}
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  ) : (
                                    <div className="text-muted small mb-1">No call assigned for this resident.</div>
                                  )}
                                </div>
                              );
                            };

                            return (
                              <>
                                {renderWeek("This Weekend", rotationCallInfo?.currentWeekendCall)}
                                {renderWeek("Next Weekend", rotationCallInfo?.upcomingWeekendCall)}
                              </>
                            );
                          })()}
                        </div>
                      </Col>
                    </Row>
                  )}
                </Card.Body>
              </Card>
            </Col>
            <Col md={12}>
              {/* Daily Hours + Past Monthly Completed Submissions */}
              <Card className="mb-4 shadow-sm border-0">
                <Card.Header className="bg-white border-0 p-3 d-flex justify-content-between align-items-start">
                  <div>
                    <h6 className="mb-0 fw-semibold">Daily Hours Worked (Last 30 Days)</h6>
                    <p className="mb-0 text-muted small">Daily totals and weekly-style stats</p>
                  </div>
                </Card.Header>
                <Card.Body className="p-0">
                  <div className="p-3">
                  {dailyWorkHours30DaysLoading ? (
                    <div
                      className="d-flex flex-column align-items-center justify-content-center"
                      style={{ minHeight: 180 }}
                    >
                      <Spinner animation="border" role="status" />
                      <div className="text-muted mt-2 small">Loading daily work hours...</div>
                    </div>
                  ) : dailyWorkHours30Days.length === 0 ? (
                    <div className="text-muted small">No work log data available.</div>
                  ) : (
                    (() => {
                      const daysWorked = dailyWorkHours30Days.filter((d) => d.hasData).length;
                      const totalHours = dailyWorkHours30Days.reduce((sum, d) => sum + (d.hours || 0), 0);
                      const avgHours = dailyWorkHours30Days.length > 0 ? totalHours / dailyWorkHours30Days.length : 0;
                      const daysOver8 = dailyWorkHours30Days.filter((d) => d.hours > 8).length;
                      const weekendDays = dailyWorkHours30Days.filter((d) => d.isWeekend && d.hasData).length;
                      const attendancePct = dailyWorkHours30Days.length
                        ? Math.round((daysWorked / dailyWorkHours30Days.length) * 100)
                        : 0;

                      return (
                        <>
                        <Row>
                          <Col md={9} lg={10}>
                      
                          <div style={{ width: "100%", height: 220 }}>
                            <ResponsiveContainer>
                              <BarChart
                                data={dailyWorkHours30Days}
                                margin={{ top: 10, right: 10, left: -10, bottom: 0 }}
                              >
                                <CartesianGrid strokeDasharray="2 6" stroke="var(--border-bg)" vertical={false} />
                                <XAxis
                                  dataKey="day"
                                  stroke="var(--border-bg)"
                                  angle={-45}
                                  textAnchor="end"
                                  height={48}
                                  tickLine={false}
                                  tick={{ fontSize: 10, fill: "var(--gray-600)" }}
                                />
                                <YAxis
                                stroke="var(--border-bg)"
                                  tickLine={false}
                                  tick={{ fontSize: 10, fill: "var(--gray-600)" }}
                                />
                                <Tooltip
                                  formatter={(value) => [`${Number(value).toFixed(2)} hours`, "Hours Worked"]}
                                  contentStyle={{
                                    backgroundColor: "var(--card-bg)",
                                    border: "1px solid var(--border-bg)",
                                    color: "var(--gray-800)",
                                    borderRadius: "8px",
                                    boxShadow: "var(--box-shadow-sm)",
                                  }}
                                  labelFormatter={(label) => label}
                                />
                                <Bar dataKey="hours" barSize={12} radius={[4, 4, 0, 0]}>
                                  {dailyWorkHours30Days.map((entry, idx) => (
                                    <Cell
                                      key={`${entry.date}-${idx}`}
                                      fill={
                                        entry.isWeekend
                                          ? "var(--red)"
                                          : entry.hours > 8
                                          ? "var(--yellow)"
                                          : "var(--green)"
                                      }
                                    />
                                  ))}
                                </Bar>
                              </BarChart>
                            </ResponsiveContainer>
                          </div>    
                          </Col>
                          <Col md={3} lg={2}>
                         

                          <div className="d-flex justify-content-md-start justify-content-center gap-2 flex-wrap mt-3">
                            <div className="d-flex align-items-center">
                              <div className="rounded me-2" style={{ width: 12, height: 12, backgroundColor: "var(--green)" }} />
                              <small className="text-muted">Normal Hrs (&le;8)</small>
                            </div>
                            <div className="d-flex align-items-center">
                              <div className="rounded me-2" style={{ width: 12, height: 12, backgroundColor: "var(--yellow)" }} />
                              <small className="text-muted">Long Hrs (&gt;8)</small>
                            </div>
                            <div className="d-flex align-items-center">
                              <div className="rounded me-2" style={{ width: 12, height: 12, backgroundColor: "var(--red)" }} />
                              <small className="text-muted">Weekend Hrs</small>
                            </div>
                          </div> 
                          </Col>
                        </Row>

                          <div className="d-flex flex-wrap justify-content-between align-items-start border-top gap-4 pt-4 px-md-2 px-0 mt-3">
                            <div className="flex-fill p-2">
                              <div className="text-muted small mb-1">Days Worked</div>
                              <div className="fw-bold" style={{ fontSize: "24px" }}>
                                {daysWorked}
                              </div>
                            </div>
                            <div className="flex-fill p-2">
                              <div className="text-muted small mb-1">Total Hours</div>
                              <div className="fw-bold" style={{ fontSize: "24px" }}>
                                {Math.round(totalHours * 100) / 100}
                              </div>
                            </div>
                            <div className="flex-fill p-2">
                              <div className="text-muted small mb-1">Avg Hours/Day</div>
                              <div className="fw-bold" style={{ fontSize: "24px" }}>
                                {Math.round(avgHours * 100) / 100}
                              </div>
                            </div>
                            <div className="flex-fill p-2">
                              <div className="text-muted small mb-1">Days &gt; 8 Hours</div>
                              <div className="fw-bold" style={{ fontSize: "24px" }}>
                                {daysOver8}
                              </div>
                            </div>
                            <div className="flex-fill p-2">
                              <div className="text-muted small mb-1">Weekend Days</div>
                              <div className="fw-bold" style={{ fontSize: "24px" }}>
                                {weekendDays}
                              </div>
                            </div>
                            <div className="flex-fill p-2">
                              <div className="text-muted small mb-1">Attendance</div>
                              <div className="fw-bold" style={{ fontSize: "24px" }}>
                                {attendancePct}%
                              </div>
                            </div>
                          </div>
                        </>
                      );
                    })()
                  )}
                  </div>
                  <hr className="my-2" />
                  <div className="p-3">

                  <div className="mb-3">
                    <h6 className="mb-0 fw-semibold">Past Monthly Completed Timesheets</h6>
                    <div className="text-muted small">Completed months (last 6)</div>
                  </div>
                  {pastMonthlyCompletedLoading ? (
                    <Spinner animation="border" size="sm" role="status" />
                  ) : null}

                
                  {pastMonthlyCompletedLoading ? (
                    <div className="text-muted small">Loading past submissions...</div>
                  ) : pastMonthlyCompleted.length === 0 ? (
                    <div className="text-muted small">No completed work-log months found.</div>
                  ) : (
                    <ListGroup variant="flush" className="border-alt rounded-3">
                      {pastMonthlyCompleted.map((item) => {
                        const label = new Date(item.year, item.month, 1).toLocaleDateString(
                          undefined,
                          { month: "long", year: "numeric" }
                        );
                        return (
                          <ListGroup.Item
                            key={`${item.month}-${item.year}`}
                            className="d-flex justify-content-between align-items-center"
                          >
                            <div>
                              <div className="fw-semibold small">{label}</div>
                              {item.lastSubmission ? (
                                <div className="text-muted small">
                                  Last submission: {item.lastSubmission}
                                </div>
                              ) : null}
                            </div>
                            <a
                              href={`/director/work-log/${id}/${item.month}/${item.year}`}
                              className="btn btn-sm btn-outline-primary"
                            >
                              View
                            </a>
                          </ListGroup.Item>
                        );
                      })}
                    </ListGroup>
                  )}
                  </div>
                </Card.Body>
              </Card>
            </Col>
            <Col md={12}>
              <Card className="mb-4 shadow-sm border-0">
                <Card.Header className="border-0 p-3 d-flex justify-content-between align-items-start">
                  <div>
                    <h6 className="mb-0 fw-semibold">Conference Attendance</h6>
                    <div className="text-muted small">
                      {moment().format("MMMM YYYY")}
                    </div>
                  </div>
                  {/* Status badges moved here */}
                  {!conferenceMonthLoading && conferenceMonthSessions.length !== 0 && (() => {
                    // Remove 'pending' from status accounting: all not recorded are 'no_record'
                    const getStatus = (sessionId) => {
                      const rec = conferenceMonthAttendanceIndex.get(String(sessionId));
                      return rec?.status || "no_record";
                    };
                    const counts = conferenceMonthSessions.reduce(
                      (acc, s) => {
                        const st = getStatus(s.id);
                        acc.total += 1;
                        if (st === "attended") acc.attended += 1;
                        else if (st === "not_attended") acc.notAttended += 1;
                        else acc.noRecord += 1; // pending also counted as noRecord here
                        return acc;
                      },
                      { total: 0, attended: 0, notAttended: 0, noRecord: 0 }
                    );
                    const total = counts.total || 0;
                    const attendedPct = total ? (counts.attended / total) * 100 : 0;
                    const missedPct = total ? (counts.notAttended / total) * 100 : 0;
                    const noRecordPct = total ? (counts.noRecord / total) * 100 : 0;

                    return (
                      <div className="d-flex flex-column gap-2" style={{ minWidth: 320 }}>
                        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                          <div className="text-muted small">
                            <b>{counts.attended}</b> attended · <b>{counts.notAttended}</b> missed ·{" "}
                            <b>{counts.noRecord}</b> no record
                          </div>
                          <div className="text-muted small">
                            Total: <b>{total}</b>
                          </div>
                        </div>

                        <div
                          className="w-100 custom-progress-bar-solid"
                          title={`Attended: ${counts.attended} · Missed: ${counts.notAttended} · No record: ${counts.noRecord} · Total: ${total}`}
                        >
                          <div className="bg-success-solid"
                            style={{
                              width: `${attendedPct}%`,
                            }}
                          />
                          <div
                            className="bg-danger-solid"
                            style={{
                              width: `${missedPct}%`,
                            }}
                          />
                          <div
                            className="bg-secondary"
                            style={{
                              width: `${noRecordPct}%`,
                            }}
                          />
                        </div>
                      </div>
                    );
                  })()}
                </Card.Header>
                <Card.Body>
                    {/* Conference Attendance (This Month) */}
                    <div className="mb-4">
                      {conferenceMonthLoading ? (
                          <Spinner animation="border" size="sm" role="status" />
                        ) : null}
                      {conferenceMonthLoading ? (
                        <div className="text-muted small">Loading conference sessions...</div>
                      ) : conferenceMonthSessions.length === 0 ? (
                        <div className="text-muted small">No conferences scheduled this month.</div>
                      ) : (
                        (() => {
                          const getStatus = (sessionId) => {
                            const rec = conferenceMonthAttendanceIndex.get(String(sessionId));
                            return rec?.status || "no_record";
                          };

                          const statusIcon = (status) => {
                            if (status === "attended") {
                              // Return a circle with border and inner circle
                              return (
                                <span
                                  title="Attended"
                                  className="custom-radial-circle-outer-success"
                                >
                                  <span
                                    className="custom-radial-circle-inner success"
                                  ></span>
                                </span>
                              );
                            }
                            if (status === "not_attended") {
                              return (
                                <span
                                  title="Not attended"
                                  className="custom-radial-circle-outer-danger"
                                >
                                  <span
                                    className="custom-radial-circle-inner danger"
                                  ></span>
                                </span>
                              );
                            }
                            return (
                              <span
                                title="No record"
                                className="custom-radial-circle-outer-secondary"
                              >
                              </span>
                            );
                          };

                          const sessionsByConferenceTitle = new Map(); // title -> sessions[]
                          conferenceMonthSessions.forEach((s) => {
                            const title = s.conferenceTitle || "Conference";
                            if (!sessionsByConferenceTitle.has(title)) sessionsByConferenceTitle.set(title, []);
                            sessionsByConferenceTitle.get(title).push(s);
                          });

                          const uniqueDates = Array.from(
                            new Set(conferenceMonthSessions.map((s) => s.date))
                          ).sort((a, b) => moment(a).valueOf() - moment(b).valueOf());

                          // title -> date -> session
                          const sessionIndex = new Map();
                          sessionsByConferenceTitle.forEach((sessions, title) => {
                            const byDate = new Map();
                            sessions.forEach((s) => {
                              // If multiple sessions fall on the same date, keep the earliest by start time.
                              const existing = byDate.get(s.date);
                              if (!existing) {
                                byDate.set(s.date, s);
                                return;
                              }
                              const aDt = moment(`${s.date} ${s.startTime || "00:00"}`, "YYYY-MM-DD HH:mm");
                              const bDt = moment(`${existing.date} ${existing.startTime || "00:00"}`, "YYYY-MM-DD HH:mm");
                              if (aDt.isBefore(bDt)) byDate.set(s.date, s);
                            });
                            sessionIndex.set(title, byDate);
                          });



                          const conferenceTitles = Array.from(sessionsByConferenceTitle.keys()).sort((a, b) =>
                            a.localeCompare(b)
                          );

                          return (
                            <>
                             
                              <div>
                                <Table responsive className="mb-0 custom-modern-responsive">
                                  <thead>
                                    <tr>
                                      <th>Conference</th>
                                      {uniqueDates.map((d) => (
                                        <th key={d} className="text-center">
                                          {moment(d).format("MMM D")}
                                        </th>
                                      ))}
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {conferenceTitles.map((title) => {
                                      const byDate = sessionIndex.get(title) || new Map();
                                      return (
                                        <tr key={title}>
                                          <td>
                                           {title}
                                          </td>
                                          {uniqueDates.map((d) => {
                                            const s = byDate.get(d) || null;
                                            if (!s) {
                                              return <td key={d} className="text-center text-muted">—</td>;
                                            }
                                            const st = getStatus(s.id);
                                            return (
                                              <td key={d} className="text-center">
                                                <span
                                                  className="d-inline-flex align-items-center justify-content-center"
                                                  style={{ width: 22, height: 22 }}
                                                  title={`${moment(s.date).format("MMM D")} · ${st}`}
                                                >
                                                  {statusIcon(st)}
                                                </span>
                                              </td>
                                            );
                                          })}
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </Table>
                              </div>
                            </>
                          );
                        })()
                      )}
                    </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        
        {/* Performance Metrics */}
        <Row className="g-4 mb-4">


          {/* Recent Activity */}
          <Col md={6}>
            <Card>
              <Card.Header className="bg-white">
                <h5 className="mb-0">Recent Activity</h5>
              </Card.Header>
              <Card.Body>
                <ListGroup variant="flush">
                  {recentActivity.map((activity, index) => (
                    <ListGroup.Item key={index}>
                      <div className="d-flex justify-content-between align-items-center">
                        <div>
                          <Badge bg={activity.type === 'case_log' ? 'primary' : 
                                    activity.type === 'evaluation' ? 'success' : 'info'}>
                            {activity.type.replace('_', ' ').toUpperCase()}
                          </Badge>
                          <span className="ms-2">{activity.description}</span>
                        </div>
                        <small className="text-muted">
                          {new Date(activity.date).toLocaleDateString()}
                        </small>
                      </div>
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              </Card.Body>
            </Card>
          </Col>

          {/* Evaluation Progress */}
          <Col md={6}>
            <Card>
              <Card.Header className="bg-white">
                <h5 className="mb-0">Evaluation Progress</h5>
              </Card.Header>
              <Card.Body>
                <div className="mb-3">
                  <div className="d-flex justify-content-between mb-1">
                    <span>Monthly Evaluations</span>
                    <span>{metrics.evaluationCompletion.toFixed(0)}%</span>
                  </div>
                  <ProgressBar 
                    now={metrics.evaluationCompletion} 
                    variant={metrics.evaluationCompletion >= 80 ? 'success' : 
                            metrics.evaluationCompletion >= 50 ? 'warning' : 'danger'} 
                  />
                </div>
                <div className="mt-4">
                  <h6>Recent Evaluations</h6>
                  <ListGroup variant="flush">
                    {evaluationData.slice(0, 3).map((evaluation, index) => (
                      <ListGroup.Item key={index}>
                        <div className="d-flex justify-content-between align-items-center">
                          <div>
                            <span>{evaluation.title}</span>
                            <Badge bg={evaluation.completed ? 'success' : 'warning'} className="ms-2">
                              {evaluation.completed ? 'Completed' : 'Pending'}
                            </Badge>
                          </div>
                          <small className="text-muted">
                            {new Date(evaluation.dueDate).toLocaleDateString()}
                          </small>
                        </div>
                      </ListGroup.Item>
                    ))}
                  </ListGroup>
      </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
        </Col>
        <Col md={3}>
          <Card className="mb-3 shadow-sm border-0">
            <Card.Header className="border-0 p-3">
              <div className="d-flex flex-column">
              <h6 className="mb-0 fw-semibold">In-Service Exams</h6>
              </div>
            </Card.Header>
            <Card.Body className="pt-0">
              {inServiceExams.length === 0 ? (
                <div className="text-muted small">No record found.</div>
              ) : (
                <div className="d-flex flex-column gap-2">
                  {inServiceExams.map((exam, index) => (
                    <div
                      key={index}
                      className="rounded-2 p-2 mb-1 bg-light"
                    >
                      <p className="text-muted small mb-0">Academic Year {academicYear}-{academicYear + 1}</p>
                      {exam.score != null && (
                        <div className="d-flex align-items-center gap-2">
                          <span className="h3 fw-bold mb-0">
                            {exam.score}
                          </span>
                          <Badge
                            bg={exam.result === 'pass' ? 'success' : 'danger'}
                            className="rounded-2"
                          >
                            {String(exam.result || '').toUpperCase() || 'N/A'}
                          </Badge>
                        </div>
                      )}
                      <div className="d-flex flex-row justify-content-between">
                        <span className="text-muted small">{exam.examDate ? new Date(exam.examDate).toLocaleDateString() : 'N/A'} </span>
                      
                      <span className="text-muted small">
                        {exam.pdfFile ? 'PDF attached' : 'No PDF'}
                      </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card.Body>
          </Card>

          <Card className="mb-3 shadow-sm border-0">
            <Card.Header className="border-0 p-3">
              <div className="d-flex flex-column">
              <h6 className="mb-0 fw-semibold">Program-wide Requirements</h6>
              <p className="text-muted small mb-0">{academicYear}-{academicYear + 1}</p></div>
            </Card.Header>
            <Card.Body className="pt-0">
              {programWide.totalRequired === 0 ? (
                <div className="text-muted small">
                  No program-wide checklist items set for this academic year.
                </div>
              ) : (
                <>
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <span className="small fw-semibold">Overall</span>
                    <span>
                      <b>{programWide.totalCompleted}</b>/{programWide.totalRequired} <span className="text-muted small">completed ({programWide.pct}%)</span>
                    </span>
                  </div>

                  <ProgressBar
                    now={programWide.pct}
                    variant={programWide.pct >= 80 ? 'success-solid' : programWide.pct >= 50 ? 'warning-solid' : 'danger-solid'}
                    className="mb-3"
                  />
                  <hr className="my-4" />

                  <div className="d-flex flex-column gap-3">
                    {programWide.categoryStats
                      .filter((c) => c.total > 0)
                      .map((c) => (
                        <div key={c.key}>
                          <div className="d-flex justify-content-between align-items-center mb-1">
                            <div className="fw-semibold small">{c.label}</div>
                          </div>
                          <div className="d-flex flex-column gap-1">
                            {c.itemsWithStatus.slice(0, 6).map((it) => (
                              <div
                                key={it.name}
                                className="d-flex justify-content-between align-items-center"
                              >
                                <span className="text-muted small">{it.name}</span>
                                <Badge bg={it.badgeVariant}>{it.statusLabel}</Badge>
                              </div>

                            ))}
                            {c.itemsWithStatus.length > 6 && (
                              <div className="text-muted small">+{c.itemsWithStatus.length - 6} more</div>
                            )}
                          </div>
                        </div>
                      ))}
                  </div>
                </>
              )}
            </Card.Body>
          </Card>
        </Col>
        </Row>
      </Container>
    </div>
  );
};

export default ResidentProfile;
